// src/services/fileParser.js
import * as pdfjsLib from 'pdfjs-dist';
import mammoth from 'mammoth';
import { ERROR_MESSAGES, FILE_CONFIG } from '@config/constants';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc =
  'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

// ---------- File Readers ----------
const readAsArrayBuffer = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(ERROR_MESSAGES.FILE_READ_ERROR));
    reader.onload = () => resolve(reader.result);
    reader.readAsArrayBuffer(file);
  });

const readAsText = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(ERROR_MESSAGES.FILE_READ_ERROR));
    reader.onload = () => resolve(String(reader.result || ''));
    reader.readAsText(file);
  });

const getExt = file =>
  (file?.name && '.' + file.name.split('.').pop().toLowerCase()) || '';

// ---------- Main Parser ----------
export async function extractTextFromFile(file) {
  if (!file) throw new Error('No file provided.');
  const ext = getExt(file);

  const allowed = Array.isArray(FILE_CONFIG.supportedFormats)
    ? FILE_CONFIG.supportedFormats
    : ['.pdf', '.docx', '.doc', '.txt'];

  if (!allowed.includes(ext)) {
    throw new Error(`${ERROR_MESSAGES.FILE_TYPE_NOT_ALLOWED} (${ext})`);
  }

  try {
    // 📄 PDF parsing
    if (ext === '.pdf') {
      const buffer = await readAsArrayBuffer(file);
      const pdf = await pdfjsLib.getDocument({ data: buffer }).promise;
      let fullText = '';
      for (let p = 1; p <= pdf.numPages; p++) {
        const page = await pdf.getPage(p);
        const content = await page.getTextContent();
        fullText +=
          content.items
            .map(it => ('str' in it ? it.str : it?.toString?.() || ''))
            .join(' ') + '\n';
      }
      return fullText.trim();
    }

    // 📘 DOCX parsing
    if (ext === '.docx') {
      const buffer = await readAsArrayBuffer(file);
      const { value } = await mammoth.extractRawText({ arrayBuffer: buffer });
      return (value || '').trim();
    }

    // 📗 Legacy DOC (fallback)
    if (ext === '.doc') {
      const text = await readAsText(file);
      return text.trim();
    }

    // 📝 Plain text
    return (await readAsText(file)).trim();
  } catch (err) {
    console.error('Parse error:', err);
    throw new Error(ERROR_MESSAGES.PARSE_ERROR);
  }
}

// ---------- Named + Default Export ----------
export const fileParser = { extractTextFromFile };
export default fileParser;
